import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { NETWORKS, NETWORK_ORDER, type Network } from '../lib/operators';
import { rankSites, RANK_EXPLANATION } from '../lib/rank';
import { cardinalIt } from '../lib/geo';
import type { Site } from '../lib/cluster';
import type { LosStatus } from '../lib/los';
import { h, fmtDeg, fmtKm, toast } from './dom';
import { getState, setState, subscribe, homeHeight } from './state';
import { openSheet, radioLabel } from './site-sheet';
import { openSiteForm } from './site-form';
import { saveSettings } from './data';
import { warmRanking } from './los-loader';

const START = { lat: 45.7236, lon: 9.0397 };
const LOS_LABEL: Record<LosStatus, string> = { libera: 'libera', marginale: 'al limite', ostruita: 'ostruita' };

function visible(s: Site): boolean {
  const st = getState();
  if (!st.networks.has(s.network)) return false;
  if (s.reliability === 'stimato' && !st.showEstimated) return false;
  const has5 = s.radios.includes('NR');
  const has4 = s.radios.includes('LTE') || s.radios.length === 0; // unknown radios: treat as 4G-capable
  return (st.radios.has('5G') && has5) || (st.radios.has('4G') && has4);
}

export function mountMap(root: HTMLElement) {
  const mapEl = h('div', { class: 'map' });
  const chips = h('div', { class: 'chips', role: 'toolbar', 'aria-label': 'Filtri' });
  const rankBox = h('div', { class: 'rank' });
  const panel = h('div', { class: 'panel' });
  let settingHome = false;

  const homeBtn = h('button', { class: 'fab', onclick: () => {
    settingHome = !settingHome;
    homeBtn.classList.toggle('active', settingHome);
    if (settingHome) toast('Tocca la mappa dove si trova la casa');
  } }, 'Imposta casa');
  const addBtn = h('button', { class: 'fab second', onclick: () => openSiteForm() }, '+ Sito');

  root.append(h('div', { class: 'map-wrap' }, mapEl, chips, homeBtn, addBtn), panel);

  const map = L.map(mapEl, { zoomControl: false, attributionControl: true }).setView([START.lat, START.lon], 13);
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '© OpenStreetMap',
    className: 'tiles',
    crossOrigin: true, // CORS tiles are cached at real size; opaque ones are padded and can fill the storage quota
  }).addTo(map);
  L.control.zoom({ position: 'bottomright' }).addTo(map);
  const layer = L.layerGroup().addTo(map);
  let homeMarker: L.Marker | null = null;

  map.on('click', async e => {
    if (!settingHome) return;
    settingHome = false;
    homeBtn.classList.remove('active');
    try {
      await saveSettings({ home: { lat: e.latlng.lat, lon: e.latlng.lng } });
      toast('Casa salvata');
    } catch (err) {
      toast((err as Error).message);
    }
  });
  map.on('contextmenu', e => openSiteForm(undefined, { lat: e.latlng.lat, lon: e.latlng.lng }));

  function drawChips() {
    const st = getState();
    const present = new Set(st.sites.map(s => s.network));
    chips.replaceChildren(
      ...NETWORK_ORDER.filter(n => present.has(n)).map(n =>
        h('button', {
          class: 'chip' + (st.networks.has(n) ? ' on' : ''),
          style: `--c:${NETWORKS[n].color}`,
          'aria-pressed': String(st.networks.has(n)),
          onclick: () => {
            const s = new Set(st.networks);
            s.has(n) ? s.delete(n) : s.add(n);
            setState({ networks: s });
          },
        }, n),
      ),
      ...(['5G', '4G'] as const).map(r =>
        h('button', {
          class: 'chip neutral' + (st.radios.has(r) ? ' on' : ''),
          'aria-pressed': String(st.radios.has(r)),
          onclick: () => {
            const s = new Set(st.radios);
            s.has(r) ? s.delete(r) : s.add(r);
            setState({ radios: s });
          },
        }, r),
      ),
      h('button', {
        class: 'chip neutral' + (st.showEstimated ? ' on' : ''),
        'aria-pressed': String(st.showEstimated),
        onclick: () => setState({ showEstimated: !st.showEstimated }),
      }, 'Stimati'),
    );
  }

  function drawMarkers() {
    const st = getState();
    layer.clearLayers();
    for (const s of st.sites) {
      if (!visible(s)) continue;
      const c = NETWORKS[s.network].color;
      const m = L.circleMarker([s.lat, s.lon], {
        radius: s.reliability === 'stimato' ? 6 : 9,
        color: c,
        weight: 3,
        fillColor: c,
        fillOpacity: s.reliability === 'stimato' ? 0.15 : 0.55,
        opacity: s.reliability === 'stimato' ? 0.55 : 1,
        dashArray: s.reliability === 'manuale' ? '4 3' : undefined,
      }).addTo(layer);
      m.bindTooltip(`${s.network} · ${radioLabel(s.radios)}`);
      m.on('click', () => openSheet(s));
    }
    const home = st.settings.home;
    if (home) {
      if (!homeMarker) {
        homeMarker = L.marker([home.lat, home.lon], {
          icon: L.divIcon({ className: 'home-icon', html: '<span>⌂</span>', iconSize: [32, 32], iconAnchor: [16, 16] }),
          keyboard: false,
        }).addTo(map);
      } else homeMarker.setLatLng([home.lat, home.lon]);
    }
  }

  function drawPanel() {
    const st = getState();
    const home = st.settings.home;
    if (!home) {
      panel.replaceChildren(
        h('h2', {}, 'Benvenuto'),
        h('p', {}, 'Tocca “Imposta casa” e poi il punto della mappa dove si trova la casa. Da lì calcolo distanze, direzioni e classifica.'),
      );
      return;
    }
    const heightInput = h('input', {
      type: 'number', inputmode: 'decimal', min: 0, max: 100, step: 0.5, value: String(homeHeight()), class: 'small-input',
      onchange: async () => {
        const v = Number(heightInput.value);
        if (!Number.isFinite(v) || v < 0 || v > 100) { toast('Altezza non valida'); return; }
        try { await saveSettings({ homeHeightM: v }); void warmRanking(); } catch (e) { toast((e as Error).message); }
      },
    });
    const statuses = new Map<string, LosStatus>();
    for (const [id, r] of st.los) statuses.set(id, r.status);
    const ranked = rankSites(home, st.sites.filter(visible), statuses);
    rankBox.replaceChildren(
      ...NETWORK_ORDER.filter(n => ranked[n]?.length).map(n =>
        h('div', { class: 'rank-net' },
          h('h3', {}, h('span', { class: 'dot', style: `background:${NETWORKS[n].color}` }), n,
            h('span', { class: 'muted small' }, ` · ${NETWORKS[n].brands.filter(b => b !== n).join(', ')}`)),
          h('ol', {},
            ...ranked[n]!.map(s =>
              h('li', {},
                h('button', { class: 'rank-item', onclick: () => { map.setView([s.lat, s.lon], 15); openSheet(s); } },
                  h('strong', {}, fmtKm(s.distanceM)),
                  h('span', {}, `${fmtDeg(s.bearing)} ${cardinalIt(s.bearing)}`),
                  h('span', { class: `los-tag los-${s.los ?? 'na'}` }, s.los ? LOS_LABEL[s.los] : '…'),
                  h('span', { class: 'muted small' }, s.reliability === 'stimato' ? 'stimato' : s.radios.length ? radioLabel(s.radios) : ''),
                ),
              ),
            ),
          ),
        ),
      ),
    );
    panel.replaceChildren(
      h('div', { class: 'panel-head' },
        h('h2', {}, 'Classifica'),
        h('label', { class: 'inline' }, 'Antenna a ', heightInput, ' m da terra'),
      ),
      h('p', { class: 'muted small' }, RANK_EXPLANATION),
      rankBox,
    );
  }

  let lastHome: unknown;
  function render(full = true) {
    drawChips();
    drawMarkers();
    drawPanel();
    const home = getState().settings.home;
    if (home && home !== lastHome) {
      if (!lastHome) map.setView([home.lat, home.lon], 13);
      lastHome = home;
      void warmRanking();
    }
    if (full) setTimeout(() => map.invalidateSize(), 0);
  }

  subscribe((s, prev) => {
    if (s.tab === 'mappa' && prev.tab !== 'mappa') setTimeout(() => map.invalidateSize(), 0);
    if (s.sites !== prev.sites || s.networks !== prev.networks || s.radios !== prev.radios || s.showEstimated !== prev.showEstimated || s.settings !== prev.settings) render(false);
    else if (s.los !== prev.los) drawPanel();
  });
  render();
}

export type { Network };
